import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Grid, Alert, CircularProgress, Button, TextField } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import {
  useCn61Reports,
  exportUpuCn61Data,
  UpuCn61ExportPayload,
  UpuCn61ExportParams,
} from './hooks';
import { CN61Table } from './components';
import CN61HtmlReportTable from './components/CN61HtmlReportTable';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { useUser } from '@/hooks/useUser';
import { NoDataFound } from '../../../common/components/NoDataFound';
import {
  MainContent,
  ReportContainer,
  ClearResultsButton,
  ButtonContainer,
  ExportButton,
} from './CN61CompoundRateReports.styled';

function CN61UpuPage() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams] = useSearchParams();
  const { data: userInfo } = useUser();

  // Get URL parameters
  const ppo = searchParams.get('ppo') || '';
  const year = searchParams.get('year') || '';
  const directionParam = searchParams.get('direction') || 'ALL';
  const direction =
    directionParam.toUpperCase() === 'INBOUND' || directionParam.toUpperCase() === 'OUTBOUND'
      ? (directionParam.toUpperCase() as 'INBOUND' | 'OUTBOUND')
      : 'ALL';

  // Get CRR data from URL parameters
  const crrDataParam = searchParams.get('crrData');
  const crrData = crrDataParam ? JSON.parse(crrDataParam) : null;

  // Get CN61 extra data from URL parameters (from CRR CSV upload)
  const cn61ExtraDataParam = searchParams.get('cn61ExtraData');
  const cn61ExtraData = cn61ExtraDataParam ? JSON.parse(cn61ExtraDataParam) : null;

  // Results state
  const [results, setResults] = useState<any[] | null>(null);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // CN61 data state - will store user input values for export
  const [cn61InputData, setCn61InputData] = useState<any>(null);

  // HTML Report data state - will store the HTML report data for display
  const [htmlReportData, setHtmlReportData] = useState<any[] | null>(null);
  const [showHtmlReport, setShowHtmlReport] = useState<boolean>(false);

  // Flag to prevent re-processing of CRR data
  const [hasProcessedCrrData, setHasProcessedCrrData] = useState<boolean>(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Currency state
  const [currency, setCurrency] = useState<string>('EUR');

  // CN61 Reports query for UPU endpoint
  const {
    data: cn61UpuData,
    isLoading: isCn61UpuLoading,
    error: cn61UpuError,
    refetch: refetchCn61Upu,
  } = useCn61Reports(
    {
      ppo: ppo,
      year: year ? parseInt(year) : 0,
      direction: direction,
      includeCrr: false, // Always false for UPU
    },
    'upu',
    !!(ppo && year) // Enable query only if we have required parameters
  );

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/upu-cn61-crr');
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Helper function to extract error message from different error structures
  const extractErrorMessage = useCallback((error: any): string => {
    if (!error) return 'Failed to load data. Please try again.';
    
    // Check if error.response.data exists (axios error structure)
    if (error?.response?.data) {
      const errorData = error.response.data;
      
      // Check if errorData is directly a string
      if (typeof errorData === 'string') {
        return errorData;
      } else if (errorData.message) {
        return errorData.message;
      } else if (errorData.error) {
        return errorData.error;
      } else if (Array.isArray(errorData)) {
        return errorData.join(', ');
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        return errorData.errors.join(', ');
      }
    } else if (error?.message) {
      return error.message;
    }
    
    return 'Failed to load data. Please try again.';
  }, []);

  const clearResults = useCallback(() => {
    setResults(null);
    setNoDataFound(false);
    setCn61InputData(null);
    setHtmlReportData(null);
    setShowHtmlReport(false);
    setHasProcessedCrrData(false); // Reset processed flag
    setCurrency('EUR'); // Reset currency to default
  }, []);

  // Validate that all present fields are filled with exactly 3 decimals
  const threeDecimalsRegex = React.useMemo(() => /^\d+\.\d{3}$/, []);
  
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false;
    return threeDecimalsRegex.test(String(val));
  }, [threeDecimalsRegex]);

  // Format value to 3 decimals (e.g., 2 -> "2.000")
  const formatToThreeDecimals = useCallback((value: number | string | null | undefined): string | null => {
    if (value === null || value === undefined || value === '') return null;
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return null;
    return numValue.toFixed(3);
  }, []);

  // Validate UPU direction data - all fields must have exactly 3 decimals
  const validateUpuDirectionData = useCallback((ratesData: any) => {
    if (!ratesData) return false;
    
    let allFieldsValid = true;
    
    // Check all format sections (P, G, E, X)
    ['formatP', 'formatG', 'formatE', 'formatX'].forEach((format) => {
      if (ratesData[format]) {
        if (ratesData[format].weight?.rate !== undefined && !isValueComplete(ratesData[format].weight.rate)) {
          allFieldsValid = false;
        }
        if (ratesData[format].items?.rate !== undefined && !isValueComplete(ratesData[format].items.rate)) {
          allFieldsValid = false;
        }
      }
    });
    
    // Check other rate fields
    ['registeredItems', 'insuredItems', 'expressItems', 'mbagsWeight'].forEach((field) => {
      if (ratesData[field]?.rate !== undefined && !isValueComplete(ratesData[field].rate)) {
        allFieldsValid = false;
      }
    });
    
    return allFieldsValid;
  }, [isValueComplete]);

  // Validate CN61 UPU data - all fields must have exactly 3 decimals
  const validateCN61UpuData = useCallback((data: any) => {
    if (!data) return false;
    
    const hasInboundData = data.inboundRates;
    const hasOutboundData = data.outboundRates;
    
    // Check if at least one direction has data
    if (!hasInboundData && !hasOutboundData) return false;
    
    // Validate inbound data if present
    if (hasInboundData) {
      const hasValidInbound = validateUpuDirectionData(hasInboundData);
      if (!hasValidInbound) return false;
    }
    
    // Validate outbound data if present
    if (hasOutboundData) {
      const hasValidOutbound = validateUpuDirectionData(hasOutboundData);
      if (!hasValidOutbound) return false;
    }
    
    return true;
  }, [validateUpuDirectionData]);

  // Function to map CRR data to CN61 table format (UPU only uses priority values since no non-priority)
  const mapCrrDataToCn61Format = useCallback((crrData: any, baseData: any) => {
    if (!crrData || !baseData) return baseData;

    const updatedCn61Data = { ...baseData };

    // Apply CRR data to inbound section if it exists
    if (updatedCn61Data.inboundRates && crrData.inbound) {
      const inboundRates = updatedCn61Data.inboundRates;

      // Apply P format values - use priority values only (UPU doesn't have non-priority)
      if (inboundRates.formatP && crrData.inbound.formatP?.priority) {
        if (inboundRates.formatP.weight) {
          inboundRates.formatP.weight.rate =
            crrData.inbound.formatP.priority.kg !== undefined
              ? formatToThreeDecimals(crrData.inbound.formatP.priority.kg) ?? inboundRates.formatP.weight.rate
              : inboundRates.formatP.weight.rate;
        }
        if (inboundRates.formatP.items) {
          inboundRates.formatP.items.rate =
            crrData.inbound.formatP.priority.item !== undefined
              ? formatToThreeDecimals(crrData.inbound.formatP.priority.item) ?? inboundRates.formatP.items.rate
              : inboundRates.formatP.items.rate;
        }
      }

      // Apply G format values - use priority values only (UPU doesn't have non-priority)
      if (inboundRates.formatG && crrData.inbound.formatG?.priority) {
        if (inboundRates.formatG.weight) {
          inboundRates.formatG.weight.rate =
            crrData.inbound.formatG.priority.kg !== undefined
              ? formatToThreeDecimals(crrData.inbound.formatG.priority.kg) ?? inboundRates.formatG.weight.rate
              : inboundRates.formatG.weight.rate;
        }
        if (inboundRates.formatG.items) {
          inboundRates.formatG.items.rate =
            crrData.inbound.formatG.priority.item !== undefined
              ? formatToThreeDecimals(crrData.inbound.formatG.priority.item) ?? inboundRates.formatG.items.rate
              : inboundRates.formatG.items.rate;
        }
      }

      // Apply E format values - use priority values only (UPU doesn't have non-priority)
      if (inboundRates.formatE && crrData.inbound.formatE?.priority) {
        if (inboundRates.formatE.weight) {
          inboundRates.formatE.weight.rate =
            crrData.inbound.formatE.priority.kg !== undefined
              ? formatToThreeDecimals(crrData.inbound.formatE.priority.kg) ?? inboundRates.formatE.weight.rate
              : inboundRates.formatE.weight.rate;
        }
        if (inboundRates.formatE.items) {
          inboundRates.formatE.items.rate =
            crrData.inbound.formatE.priority.item !== undefined
              ? formatToThreeDecimals(crrData.inbound.formatE.priority.item) ?? inboundRates.formatE.items.rate
              : inboundRates.formatE.items.rate;
        }
      }
    }

    // Apply CRR data to outbound section if it exists
    if (updatedCn61Data.outboundRates && crrData.outbound) {
      const outboundRates = updatedCn61Data.outboundRates;

      // Apply P format values - use priority values only (UPU doesn't have non-priority)
      if (outboundRates.formatP && crrData.outbound.formatP?.priority) {
        if (outboundRates.formatP.weight) {
          outboundRates.formatP.weight.rate =
            crrData.outbound.formatP.priority.kg !== undefined
              ? formatToThreeDecimals(crrData.outbound.formatP.priority.kg) ?? outboundRates.formatP.weight.rate
              : outboundRates.formatP.weight.rate;
        }
        if (outboundRates.formatP.items) {
          outboundRates.formatP.items.rate =
            crrData.outbound.formatP.priority.item !== undefined
              ? formatToThreeDecimals(crrData.outbound.formatP.priority.item) ?? outboundRates.formatP.items.rate
              : outboundRates.formatP.items.rate;
        }
      }

      // Apply G format values - use priority values only (UPU doesn't have non-priority)
      if (outboundRates.formatG && crrData.outbound.formatG?.priority) {
        if (outboundRates.formatG.weight) {
          outboundRates.formatG.weight.rate =
            crrData.outbound.formatG.priority.kg !== undefined
              ? formatToThreeDecimals(crrData.outbound.formatG.priority.kg) ?? outboundRates.formatG.weight.rate
              : outboundRates.formatG.weight.rate;
        }
        if (outboundRates.formatG.items) {
          outboundRates.formatG.items.rate =
            crrData.outbound.formatG.priority.item !== undefined
              ? formatToThreeDecimals(crrData.outbound.formatG.priority.item) ?? outboundRates.formatG.items.rate
              : outboundRates.formatG.items.rate;
        }
      }

      // Apply E format values - use priority values only (UPU doesn't have non-priority)
      if (outboundRates.formatE && crrData.outbound.formatE?.priority) {
        if (outboundRates.formatE.weight) {
          outboundRates.formatE.weight.rate =
            crrData.outbound.formatE.priority.kg !== undefined
              ? formatToThreeDecimals(crrData.outbound.formatE.priority.kg) ?? outboundRates.formatE.weight.rate
              : outboundRates.formatE.weight.rate;
        }
        if (outboundRates.formatE.items) {
          outboundRates.formatE.items.rate =
            crrData.outbound.formatE.priority.item !== undefined
              ? formatToThreeDecimals(crrData.outbound.formatE.priority.item) ?? outboundRates.formatE.items.rate
              : outboundRates.formatE.items.rate;
        }
      }
    }

    return updatedCn61Data;
  }, [formatToThreeDecimals]);

  // Handle CN61 data changes
  const handleCN61DataChange = useCallback((updatedData: any) => {
    // Update the results with the modified data
    setResults([updatedData]);
    // Also update the CN61 input data for export
    setCn61InputData(updatedData);
  }, []);

  // Helper function to create default UPU CN61 structure with all rates set to 0
  const createDefaultUpuCn61Structure = useCallback(() => {
    return {
      formatP: {
        weight: { rate: 0 },
        items: { rate: 0 },
      },
      formatG: {
        weight: { rate: 0 },
        items: { rate: 0 },
      },
      formatE: {
        weight: { rate: 0 },
        items: { rate: 0 },
      },
      formatX: {
        weight: { rate: 0 },
        items: { rate: 0 },
      },
      registeredItems: { rate: 0 },
      insuredItems: { rate: 0 },
      expressItems: { rate: 0 },
      mbagsWeight: { rate: 0 },
    };
  }, []);

  // Helper function to merge actual data with default structure
  const mergeUpuCn61Data = useCallback((actualData: any, defaultStructure: any) => {
    if (!actualData) return defaultStructure;

    const merged = { ...defaultStructure };

    // Merge formatP
    if (actualData.formatP) {
      if (actualData.formatP.weight?.rate !== undefined) {
        merged.formatP.weight.rate = actualData.formatP.weight.rate;
      }
      if (actualData.formatP.items?.rate !== undefined) {
        merged.formatP.items.rate = actualData.formatP.items.rate;
      }
    }

    // Merge formatG
    if (actualData.formatG) {
      if (actualData.formatG.weight?.rate !== undefined) {
        merged.formatG.weight.rate = actualData.formatG.weight.rate;
      }
      if (actualData.formatG.items?.rate !== undefined) {
        merged.formatG.items.rate = actualData.formatG.items.rate;
      }
    }

    // Merge formatE
    if (actualData.formatE) {
      if (actualData.formatE.weight?.rate !== undefined) {
        merged.formatE.weight.rate = actualData.formatE.weight.rate;
      }
      if (actualData.formatE.items?.rate !== undefined) {
        merged.formatE.items.rate = actualData.formatE.items.rate;
      }
    }

    // Merge formatX
    if (actualData.formatX) {
      if (actualData.formatX.weight?.rate !== undefined) {
        merged.formatX.weight.rate = actualData.formatX.weight.rate;
      }
      if (actualData.formatX.items?.rate !== undefined) {
        merged.formatX.items.rate = actualData.formatX.items.rate;
      }
    }

    // Merge other fields
    if (actualData.registeredItems?.rate !== undefined) {
      merged.registeredItems.rate = actualData.registeredItems.rate;
    }
    if (actualData.insuredItems?.rate !== undefined) {
      merged.insuredItems.rate = actualData.insuredItems.rate;
    }
    if (actualData.expressItems?.rate !== undefined) {
      merged.expressItems.rate = actualData.expressItems.rate;
    }
    if (actualData.mbagsWeight?.rate !== undefined) {
      merged.mbagsWeight.rate = actualData.mbagsWeight.rate;
    }

    return merged;
  }, []);

  // Handle export functionality
  const handleExport = useCallback(
    async (format: 'html' | 'pdf') => {
      if (!results || results.length === 0) {
        showSnackbar('No data available to export', 'error');
        return;
      }

      if (!cn61InputData) {
        showSnackbar('No CN61 data available for export', 'error');
        return;
      }

      // Validate CN61 UPU data before export
      const hasValidData = validateCN61UpuData(cn61InputData);
      if (!hasValidData) {
        showSnackbar('Please fill all CN61 UPU TD rates with exactly 3 decimals before exporting', 'error');
        return;
      }

      try {
        setIsLoading(true);

        // Create separate default structures for each direction to avoid shared reference mutation
        const inboundDefaultStructure = createDefaultUpuCn61Structure();
        const outboundDefaultStructure = createDefaultUpuCn61Structure();

        // Merge actual data with default structure for all sections
        const inboundData = mergeUpuCn61Data(cn61InputData.inboundRates, inboundDefaultStructure);
        const outboundData = mergeUpuCn61Data(cn61InputData.outboundRates, outboundDefaultStructure);

        // Prepare the request body with all sections properly populated
        const exportPayload: UpuCn61ExportPayload = {
          inboundUpuCn61TdRatesDto: inboundData,
          outboundUpuCn61TdRatesDto: outboundData,
          orCreateInboundUpuCn61TdRatesDto: inboundData,
          orCreateOutboundUpuCn61TdRatesDto: outboundData,
        };

        // Prepare export parameters
        const exportParams: UpuCn61ExportParams = {
          direction: direction,
          year: year,
          designatedOperator: ppo,
          formatType: format,
          currency: currency,
        };

        // Use the hook function to make the request
        const response = await exportUpuCn61Data(exportPayload, exportParams);

        if (response.ok) {
          if (format === 'html') {
            // Handle HTML response - store data for display
            const htmlContent = await response.text();
            try {
              const htmlData = JSON.parse(htmlContent);
              // Ensure it's an array
              const dataArray = Array.isArray(htmlData) ? htmlData : [htmlData];

              // Add currency and country information to the data if not already present
              const dataWithCurrency = dataArray.map((item) => ({
                ...item,
                currency: item.currency || currency,
                fromCountry: ppo || 'Origin',
                toCountry: userInfo?.designatedOperator || 'Destination',
              }));

              setHtmlReportData(dataWithCurrency);
              setShowHtmlReport(true);
              showSnackbar('HTML report generated successfully!', 'success');
            } catch (error) {
              // If it's not JSON, treat as HTML content
              const newWindow = window.open('', '_blank');
              if (newWindow) {
                newWindow.document.write(htmlContent);
                newWindow.document.close();
              }
              showSnackbar('HTML report generated successfully!', 'success');
            }
          } else if (format === 'pdf') {
            // Handle PDF response
            const blob = await response.blob();
            
            // Validate blob size
            if (blob.size === 0) {
              showSnackbar('Received empty file from server', 'error');
              return;
            }
            
            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `cn61-upu-report-${ppo}-${year}.pdf`;
            
            if (contentDisposition) {
              const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
              if (filenameMatch && filenameMatch[2]) {
                filename = decodeURIComponent(filenameMatch[2]);
              }
            }
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            
            // Cleanup after a short delay
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }, 100);
            
            showSnackbar(`PDF report exported successfully!`, 'success');
          }
        } else {
          const errorText = await response.text();
          showSnackbar(`Export failed: ${errorText}`, 'error');
        }
      } catch (error) {
        showSnackbar('An error occurred while exporting the report', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [
      results,
      cn61InputData,
      direction,
      year,
      ppo,
      showSnackbar,
      createDefaultUpuCn61Structure,
      mergeUpuCn61Data,
      validateCN61UpuData,
      userInfo?.designatedOperator,
      currency,
    ]
  );

  // Toggle between editable table and HTML report view
  const toggleView = useCallback(() => {
    setShowHtmlReport(!showHtmlReport);
  }, [showHtmlReport]);

  // Effect to handle data loading when component mounts or parameters change
  useEffect(() => {
    if (ppo && year) {
      setIsLoading(true);
      setNoDataFound(false);
      setResults(null);
    }
  }, [ppo, year]);

  // Effect to handle API response (same approach as IPC CN61ReimsPage)
  useEffect(() => {
    if (cn61UpuData && !hasProcessedCrrData) {
      // Check if we have CN61 rates data
      const hasNormalData = cn61UpuData.inboundRates || cn61UpuData.outboundRates;

      if (hasNormalData) {
        // Apply CRR data if coming from CRR page (same approach as IPC)
        let finalData = cn61UpuData;
        if (crrData) {
          finalData = mapCrrDataToCn61Format(crrData, cn61UpuData);
        }

        // Apply CN61 extra data from CRR CSV upload (Format X, Registered, Insured, Express, M-Bags)
        if (cn61ExtraData) {
          // Apply inbound CN61 extra data
          if (cn61ExtraData.inboundRates && finalData.inboundRates) {
            const inboundRates = finalData.inboundRates;
            const extraInbound = cn61ExtraData.inboundRates;

            // Apply each extra field (formatX, registeredItems, insuredItems, expressItems, mbagsWeight)
            ['formatX', 'registeredItems', 'insuredItems', 'expressItems', 'mbagsWeight'].forEach(fieldKey => {
              if (extraInbound[fieldKey]) {
                // Initialize the field if it doesn't exist
                if (!inboundRates[fieldKey]) {
                  inboundRates[fieldKey] = {};
                }
                // Apply rate data if present (for items-only or weight-only fields)
                if (extraInbound[fieldKey].rate !== undefined && extraInbound[fieldKey].rate !== null) {
                  inboundRates[fieldKey].rate = extraInbound[fieldKey].rate;
                }
                // Apply weight data if present (for formatX)
                if (extraInbound[fieldKey].weight && extraInbound[fieldKey].weight.rate !== undefined) {
                  if (!inboundRates[fieldKey].weight) {
                    inboundRates[fieldKey].weight = {};
                  }
                  inboundRates[fieldKey].weight.rate = extraInbound[fieldKey].weight.rate;
                }
                // Apply items data if present (for formatX)
                if (extraInbound[fieldKey].items && extraInbound[fieldKey].items.rate !== undefined) {
                  if (!inboundRates[fieldKey].items) {
                    inboundRates[fieldKey].items = {};
                  }
                  inboundRates[fieldKey].items.rate = extraInbound[fieldKey].items.rate;
                }
              }
            });
          }

          // Apply outbound CN61 extra data
          if (cn61ExtraData.outboundRates && finalData.outboundRates) {
            const outboundRates = finalData.outboundRates;
            const extraOutbound = cn61ExtraData.outboundRates;

            // Apply each extra field (formatX, registeredItems, insuredItems, expressItems, mbagsWeight)
            ['formatX', 'registeredItems', 'insuredItems', 'expressItems', 'mbagsWeight'].forEach(fieldKey => {
              if (extraOutbound[fieldKey]) {
                // Initialize the field if it doesn't exist
                if (!outboundRates[fieldKey]) {
                  outboundRates[fieldKey] = {};
                }
                // Apply rate data if present (for items-only or weight-only fields)
                if (extraOutbound[fieldKey].rate !== undefined && extraOutbound[fieldKey].rate !== null) {
                  outboundRates[fieldKey].rate = extraOutbound[fieldKey].rate;
                }
                // Apply weight data if present (for formatX)
                if (extraOutbound[fieldKey].weight && extraOutbound[fieldKey].weight.rate !== undefined) {
                  if (!outboundRates[fieldKey].weight) {
                    outboundRates[fieldKey].weight = {};
                  }
                  outboundRates[fieldKey].weight.rate = extraOutbound[fieldKey].weight.rate;
                }
                // Apply items data if present (for formatX)
                if (extraOutbound[fieldKey].items && extraOutbound[fieldKey].items.rate !== undefined) {
                  if (!outboundRates[fieldKey].items) {
                    outboundRates[fieldKey].items = {};
                  }
                  outboundRates[fieldKey].items.rate = extraOutbound[fieldKey].items.rate;
                }
              }
            });
          }
        }

        // Show appropriate success message
        if (crrData) {
          const hasExtraData = cn61ExtraData && 
            (Object.keys(cn61ExtraData.inboundRates || {}).length > 0 ||
             Object.keys(cn61ExtraData.outboundRates || {}).length > 0);
          
          if (hasExtraData) {
            showSnackbar('CN61 UPU data loaded with CRR totals and extra fields!', 'success');
          } else {
            showSnackbar('CN61 UPU data loaded and pre-populated with CRR totals!', 'success');
          }
        } else {
          showSnackbar('CN61 UPU data loaded successfully!', 'success');
        }

        // Store the CN61 input data for editing and future exports
        setCn61InputData(finalData);
        // Initialize currency from data if available
        if (finalData.currency) {
          setCurrency(finalData.currency);
        }
        setResults([finalData]); // Wrap in array to maintain compatibility
        setNoDataFound(false);
        setHasProcessedCrrData(true); // Mark as processed
      } else {
        setResults(null);
        setNoDataFound(true);
        showSnackbar('No data found for the selected criteria', 'info');
      }
      setIsLoading(false);
    }
  }, [cn61UpuData, crrData, cn61ExtraData, mapCrrDataToCn61Format, showSnackbar, hasProcessedCrrData]);

  // Effect to handle API errors
  useEffect(() => {
    if (cn61UpuError) {
      setResults(null);
      setNoDataFound(true);
      setIsLoading(false);
    }
  }, [cn61UpuError]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='cn61-upu-page-container'>
      <Grid container data-testid='cn61-upu-page-grid-container'>
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid='cn61-upu-page-section-bar-container'>
          <SectionBar
            title='CN61 UPU'
            subtitle='UPU TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            data-testid='cn61-upu-page-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid='cn61-upu-page-main-row'>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid='cn61-upu-page-sidebar-container'>
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='cn61-upu-page-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='cn61-upu-page-main-content'>
            <ReportContainer data-testid='cn61-upu-page-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='cn61-upu-page-header'
              >
                <Box>
                  <Typography
                    variant='h4'
                    sx={{ fontWeight: 600, color: '#063142' }}
                    data-testid='cn61-upu-page-title'
                  >
                    CN61 UPU
                  </Typography>
                  {crrData && (
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#00ACE7',
                        fontWeight: 500,
                        mt: 0.5,
                        fontSize: '13px',
                      }}
                      data-testid='cn61-upu-page-crr-populated-text'
                    >
                      Pre-populated with CRR totals
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid='cn61-upu-page-header-actions'>
                  <Button
                    variant='text'
                    onClick={() => {
                      navigate('/upu-cn61-crr');
                    }}
                    sx={{
                      color: '#00ACE7',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#E3F7FE',
                      },
                    }}
                    data-testid='cn61-upu-page-back-btn'
                  >
                    ← Back to {crrData ? 'CRR' : 'Main'}
                  </Button>
                  {(results || noDataFound) && (
                    <ClearResultsButton
                      variant='outlined'
                      onClick={clearResults}
                      data-testid='cn61-upu-page-clear-results-btn'
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Parameter Display */}
              {ppo && year && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }} data-testid='cn61-upu-page-params'>
                  <Typography variant='h6' sx={{ fontWeight: 600, color: '#063142', mb: 1 }}>
                    Current Parameters
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box data-testid='cn61-upu-page-param-ppo'>
                      <Typography variant='body2' sx={{ color: '#666', fontSize: '12px' }}>
                        Designated Operator
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {ppo}
                      </Typography>
                    </Box>
                    <Box data-testid='cn61-upu-page-param-year'>
                      <Typography variant='body2' sx={{ color: '#666', fontSize: '12px' }}>
                        Year
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {year}
                      </Typography>
                    </Box>
                    <Box data-testid='cn61-upu-page-param-direction'>
                      <Typography variant='body2' sx={{ color: '#666', fontSize: '12px' }}>
                        Direction
                      </Typography>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {direction}
                      </Typography>
                    </Box>
                    {crrData && (
                      <Box data-testid='cn61-upu-page-param-source'>
                        <Typography variant='body2' sx={{ color: '#666', fontSize: '12px' }}>
                          Data Source
                        </Typography>
                        <Typography variant='body1' sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          CRR Pre-populated
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Loading State */}
              {(isLoading || isCn61UpuLoading) && (
                <Box
                  sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
                  data-testid='cn61-upu-page-loading-section'
                >
                  <CircularProgress size={24} data-testid='cn61-upu-page-loading-spinner' />
                  <Typography sx={{ ml: 2 }} data-testid='cn61-upu-page-loading-text'>Loading CN61 UPU data...</Typography>
                </Box>
              )}

              {/* Error Section */}
              {cn61UpuError && (
                <Alert severity='error' sx={{ mb: 3 }} data-testid='cn61-upu-page-error-alert'>
                  {extractErrorMessage(cn61UpuError)}
                </Alert>
              )}

              {/* No Data Found Section */}
              {noDataFound && !isLoading && !isCn61UpuLoading && (
                <NoDataFound
                  title='No Data Available'
                  message='No CN61 UPU data was found for the selected criteria.'
                  onClearResults={clearResults}
                  data-testid='cn61-upu-page-no-data-found'
                />
              )}

              {/* Results Section - Show tables when results exist */}
              {results && !noDataFound && (
                <Box sx={{ mt: 6 }} data-testid='cn61-upu-page-results-section'>
                  <Box
                    sx={{
                      borderBottom: '2px solid #E1E8ED',
                      pb: 2,
                      mb: 4,
                    }}
                    data-testid='cn61-upu-page-results-divider'
                  ></Box>

                  {/* Currency Input and Export Buttons */}
                  <Box sx={{ mb: 3 }} data-testid='cn61-upu-page-action-buttons'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TextField
                        label="Currency"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                        size="small"
                        sx={{ minWidth: 120 }}
                        inputProps={{
                          maxLength: 3,
                          style: { textTransform: 'uppercase' }
                        }}
                        data-testid='cn61-upu-page-currency-input'
                      />
                    </Box>
                    <ButtonContainer>
                      <ExportButton
                        variant='contained'
                        onClick={() => handleExport('html')}
                        disabled={isLoading || isCn61UpuLoading}
                        data-testid='cn61-upu-page-export-html-btn'
                      >
                        {isLoading || isCn61UpuLoading ? <CircularProgress size={16} data-testid='cn61-upu-page-export-html-spinner' /> : 'HTML'}
                      </ExportButton>
                      <ExportButton
                        variant='contained'
                        onClick={() => handleExport('pdf')}
                        disabled={isLoading || isCn61UpuLoading}
                        data-testid='cn61-upu-page-export-pdf-btn'
                      >
                        {isLoading || isCn61UpuLoading ? <CircularProgress size={16} data-testid='cn61-upu-page-export-pdf-spinner' /> : 'PDF'}
                      </ExportButton>
                      {htmlReportData && (
                        <ExportButton
                          variant='outlined'
                          onClick={toggleView}
                          sx={{
                            backgroundColor: showHtmlReport ? '#e3f2fd' : 'transparent',
                            color: showHtmlReport ? '#1976d2' : 'inherit',
                          }}
                          data-testid='cn61-upu-page-toggle-view-btn'
                        >
                          {showHtmlReport ? 'Show Rates' : 'Show HTML Report'}
                        </ExportButton>
                      )}
                    </ButtonContainer>
                  </Box>

                  {/* Results content - Show either editable table or HTML report */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid='cn61-upu-page-results-content'>
                    {showHtmlReport && htmlReportData ? (
                      /* HTML Report View */
                      <Box data-testid='cn61-upu-page-html-report-section'>
                        <CN61HtmlReportTable
                          data={htmlReportData}
                          title='CN61 UPU Terminal Dues Report'
                          direction={direction.toLowerCase() as 'inbound' | 'outbound' | 'unknown'}
                        />
                      </Box>
                    ) : (
                      /* Editable Table View */
                      results.map((data, index) => (
                        <Box key={index} data-testid={`cn61-upu-page-table-section-${index}`}>
                          <CN61Table
                            data={data}
                            onDataChange={handleCN61DataChange}
                            onError={showSnackbar}
                            onSuccess={(message) => showSnackbar(message, 'success')}
                            currentUserPpo={userInfo?.designatedOperator}
                          />
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='cn61-upu-page-snackbar'
      />
    </Box>
  );
}

export default CN61UpuPage;
